import { useState, useEffect } from 'react';
import { KeyRound, Home, Dumbbell, Library, LogOut, Sun, Moon, CalendarDays, ClipboardList } from 'lucide-react';
import LoginScreen from './components/auth/LoginScreen';
import ChangePasswordModal from './components/account/ChangePasswordModal';
import ExerciseLibrary from './components/exercises/ExerciseLibrary';
import WorkoutView from './components/workout/WorkoutView';
import CalendarView from './components/calendar/CalendarView';
import ProgramView from './components/program/ProgramView';
import HomeView from './components/home/HomeView';
import RegisterScreen from './components/auth/RegisterScreen';
import ForgotPasswordScreen from './components/auth/ForgotPasswordScreen';
import ResetPasswordScreen from './components/auth/ResetPasswordScreen';
import VerifyEmailScreen from './components/auth/VerifyEmailScreen';
import { useBackHandler } from './lib/backNav';

// API base — in dev, CRA proxy handles this; in prod, same origin
const API_BASE = process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3020/api';

const request = async (method, endpoint, { data, token } = {}) => {
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  const response = await fetch(`${API_BASE}${endpoint}`, {
    method,
    headers,
    ...(data !== undefined ? { body: JSON.stringify(data) } : {}),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    const e = new Error(err.error || `HTTP ${response.status}`);
    e.code = err.code;          // e.g. 'unverified'
    e.status = response.status;
    throw e;
  }
  const refreshedToken = response.headers.get('X-Refreshed-Token');
  if (refreshedToken) localStorage.setItem('swolepump_token', refreshedToken);
  return response.json();
};

const api = {
  get: (endpoint, token) => request('GET', endpoint, { token }),
  post: (endpoint, data, token) => request('POST', endpoint, { data, token }),
  put: (endpoint, data, token) => request('PUT', endpoint, { data, token }),
  delete: (endpoint, token) => request('DELETE', endpoint, { token }),
};

// Map the initial URL path to a route. Token links land on /verify and /reset-password.
const routeFromPath = () => {
  const p = window.location.pathname;
  if (p === '/verify') return 'verify';
  if (p === '/reset-password') return 'reset';
  return 'auth';
};

const App = () => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(true);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [immersive, setImmersive] = useState(false);        // workout player full-screen mode
  const [currentView, setCurrentView] = useState('home');   // 'home' | 'workout' | 'exercises'
  const [workoutDate, setWorkoutDate] = useState(null);     // which scheduled day to run (null = today)
  const [route, setRoute] = useState(routeFromPath);   // 'auth' | 'verify' | 'reset'
  const [authView, setAuthView] = useState('login');   // 'login' | 'register' | 'forgot'
  const [restoring, setRestoring] = useState(true);

  // Sync dark mode class on <html>
  useEffect(() => {
    document.documentElement.classList.toggle('dark', isDarkMode);
  }, [isDarkMode]);

  // Restore a saved session if the token still works
  useEffect(() => {
    const savedToken = localStorage.getItem('swolepump_token');
    if (savedToken) {
      api.get('/me', savedToken)
        .then((me) => { setUser(me); setToken(savedToken); if (typeof me.darkMode === 'boolean') setIsDarkMode(me.darkMode); })
        .catch(() => {
          localStorage.removeItem('swolepump_token');
          localStorage.removeItem('swolepump_user');
        })
        .finally(() => setRestoring(false));
    } else {
      setRestoring(false);
    }
  }, []);

  const handleLogin = (userData, userToken) => {
    setUser(userData);
    setToken(userToken);
    setMenuOpen(false);
    if (typeof userData.darkMode === 'boolean') setIsDarkMode(userData.darkMode);
    localStorage.setItem('swolepump_user', JSON.stringify(userData));
    localStorage.setItem('swolepump_token', userToken);
  };

  // Toggle theme and persist the choice to the user's account.
  const toggleTheme = () => {
    setIsDarkMode((prev) => {
      const next = !prev;
      if (token) api.put('/me/preferences', { dark_mode: next }, token).catch(() => {});
      return next;
    });
  };

  const handleLogout = () => {
    setUser(null);
    setToken(null);
    setMenuOpen(false); // Logout lives inside the menu — close it so it isn't open on next login
    localStorage.removeItem('swolepump_user');
    localStorage.removeItem('swolepump_token');
  };

  // Back closes the account menu first.
  useBackHandler(menuOpen, () => setMenuOpen(false));

  // Called by token screens when finished — clears the URL and returns to the auth flow.
  const finishTokenRoute = (nextView = 'login') => {
    window.history.replaceState({}, '', '/');
    setAuthView(nextView);
    setRoute('auth');
  };

  // Token-link routes take precedence (work whether or not someone is signed in).
  if (route === 'verify') return <VerifyEmailScreen api={api} onDone={finishTokenRoute} />;
  if (route === 'reset') return <ResetPasswordScreen api={api} onDone={finishTokenRoute} />;

  if (restoring) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground">
        <img src="/icon-192.png" alt="SwolePump" className="h-12 w-12 rounded-xl animate-pulse" />
      </div>
    );
  }

  if (!user) {
    if (authView === 'register') return <RegisterScreen api={api} onNav={setAuthView} />;
    if (authView === 'forgot') return <ForgotPasswordScreen api={api} onNav={setAuthView} />;
    return <LoginScreen api={api} onLogin={handleLogin} onNav={setAuthView} />;
  }

  const roleLabel = { client: 'Client', trainer: 'Trainer', admin: 'Admin', power_user: 'Power user' }[user.role] || user.role;
  const initial = (user.displayName || user.email || '?').trim().charAt(0).toUpperCase();
  const showTabs = !immersive;

  const isCoach = user.role === 'trainer' || user.role === 'admin';
  const canProgram = isCoach || user.role === 'power_user'; // power users self-assign saved programs
  const TABS = [
    { key: 'home', label: 'Home', icon: Home },
    { key: 'workout', label: 'Workout', icon: Dumbbell },
    { key: 'calendar', label: 'Calendar', icon: CalendarDays },
    ...(canProgram ? [{ key: 'program', label: 'Program', icon: ClipboardList }] : []),
    { key: 'exercises', label: 'Exercises', icon: Library },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* Top header — always shown so the iOS top safe area / Dynamic Island
          is respected consistently, including in the workout player. */}
      <header
        className={`sticky top-0 z-40 border-b ${isDarkMode ? 'border-white/10 bg-[#0a0a0a]' : 'border-border bg-white'}`}
        style={{ paddingTop: 'env(safe-area-inset-top)' }}
      >
          <div className="h-14 px-4 flex items-center justify-between">
            <div className="flex items-center">
              <img src={isDarkMode ? '/swolepump-header.png' : '/swolepump-header-light.png'} alt="SwolePump" className="h-9 w-auto" />
            </div>
            <div className="relative">
              <button
                onClick={() => setMenuOpen((o) => !o)}
                className="h-9 w-9 rounded-full bg-primary/15 text-primary font-semibold flex items-center justify-center"
                aria-label="Account menu"
              >
                {initial}
              </button>
              {menuOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />
                  <div className="absolute right-0 mt-2 w-60 rounded-lg border bg-card shadow-lg z-50 overflow-hidden">
                    <div className="px-4 py-3 border-b">
                      <div className="font-medium truncate">{user.displayName}</div>
                      <div className="text-xs text-muted-foreground truncate">{user.email} · {roleLabel}</div>
                    </div>
                    <MenuItem
                      icon={isDarkMode ? Sun : Moon}
                      label={isDarkMode ? 'Light mode' : 'Dark mode'}
                      onClick={() => { toggleTheme(); setMenuOpen(false); }}
                    />
                    <MenuItem
                      icon={KeyRound}
                      label="Change password"
                      onClick={() => { setShowChangePassword(true); setMenuOpen(false); }}
                    />
                    <MenuItem icon={LogOut} label="Logout" danger onClick={handleLogout} />
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

      {showChangePassword && (
        <ChangePasswordModal api={api} token={token} onClose={() => setShowChangePassword(false)} />
      )}

      {/* Main */}
      <main className={`flex-1 ${showTabs ? 'pb-24' : ''}`}>
        {currentView === 'workout' ? (
          <WorkoutView api={api} token={token} date={workoutDate} onExit={() => setCurrentView('home')} onImmersiveChange={setImmersive} />
        ) : currentView === 'calendar' ? (
          <CalendarView api={api} token={token} onStartWorkout={(date) => { setWorkoutDate(date || null); setCurrentView('workout'); }} />
        ) : currentView === 'program' && canProgram ? (
          <ProgramView api={api} token={token} user={user} />
        ) : currentView === 'exercises' ? (
          <ExerciseLibrary api={api} token={token} user={user} />
        ) : (
          <HomeView api={api} token={token} user={user} onStartWorkout={() => { setWorkoutDate(null); setCurrentView('workout'); }} />
        )}
      </main>

      {/* Bottom tab bar (respects the iOS bottom safe area; hidden during the workout player) */}
      {showTabs && (
        <nav
          className="fixed bottom-0 inset-x-0 z-40 border-t bg-card"
          style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        >
          <div className="max-w-md mx-auto h-16 flex">
            {TABS.map((t) => {
              const active = currentView === t.key;
              const Icon = t.icon;
              return (
                <button
                  key={t.key}
                  onClick={() => { if (t.key === 'workout') setWorkoutDate(null); setCurrentView(t.key); }}
                  className={`flex-1 flex flex-col items-center justify-center gap-0.5 ${active ? 'text-primary' : 'text-muted-foreground'}`}
                >
                  <Icon className="h-6 w-6" strokeWidth={active ? 2.4 : 2} />
                  <span className="text-[11px] font-medium">{t.label}</span>
                </button>
              );
            })}
          </div>
        </nav>
      )}
    </div>
  );
};

const MenuItem = ({ icon: Icon, label, onClick, danger }) => (
  <button
    onClick={onClick}
    className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-accent text-left ${danger ? 'text-destructive' : ''}`}
  >
    <Icon className="h-4 w-4" />
    {label}
  </button>
);

export default App;
